import type { Session } from '@orgus/protocol';
import { runsOf, unitsOf } from './model.ts';
import { summaryLine } from '../../lib/plainText.ts';

export interface LiveRow {
  key: string;
  sessionId: string;
  runId: string | null;
  title: string;
  line: string;
  machineId: string;
  projectId: string | null;
  waiting: boolean;
  startedAt: number;
  toolCalls: number | null;
}

/**
 * Stable identity order: clock ticks and tool updates never move a row under the pointer.
 * `askedAt`: when each session's ask began (the needs-you items the Inbox reads, by session id).
 * A waiting row times its wait from there, so it reads the same as the Inbox (QA-4), never the
 * session's age; without one it falls back to the session's start.
 */
export function liveRows(sessions: readonly Session[], askedAt?: ReadonlyMap<string, number>): LiveRow[] {
  return sessions.flatMap((s) => {
    const base = { sessionId: s.id, machineId: s.machineId, projectId: s.projectId };
    const rows: LiveRow[] = s.status === 'working' || s.status === 'waiting' ? [{
      ...base, key: `session:${s.id}`, runId: null, title: s.title || 'Agent session',
      line: summaryLine(s.lastText) || (s.status === 'waiting' ? 'Waiting for your answer' : 'Working; activity details not reported'),
      waiting: s.status === 'waiting', startedAt: (s.status === 'waiting' ? askedAt?.get(s.id) : undefined) ?? s.startedAt, toolCalls: s.toolCalls,
    }] : [];
    for (const r of runsOf(s).flatMap(unitsOf)) {
      if (r.status !== 'running') continue;
      rows.push({ ...base, key: `run:${s.id}:${r.id}`, runId: r.id, title: r.title,
        line: summaryLine(r.lastText ?? '') || r.phase || 'Working; activity details not reported',
        waiting: false, startedAt: r.startedAt, toolCalls: r.toolCalls });
    }
    return rows;
  }).sort((a, b) => a.key.localeCompare(b.key));
}
